import * as React from "react";
import { useMemo } from "react";
import { Intake } from "../../models/Intake";
import { DeleteIntakeRequest } from "../../models/Jobs/JobRequest";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { JobType } from "../../models/Jobs/JobType";
import { JobEntityType } from "../../models/Jobs/JobEntityType";
import { ActiveIntakeJobsExplorer } from "../../pages/IntakesPage";
import { UserRole } from "../../models/User";
import { MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS } from "../common/ConfirmMigrationEntityBlockUpdateModal";
import IntakesRepository from "../../repositories/IntakesRepository";
import ConfirmIntakeFlagUpdateModal from "./ConfirmIntakeFlagUpdateModal";
import MigrationEntityTable, { type MigrationEntityTableConfig } from "../common/MigrationEntityTable/MigrationEntityTable";
import { QUERY_KEYS } from "../../constants/QueryKeys";

interface IntakesTableProps {
  organizationId: string;
  activeJobsExplorer: ActiveIntakeJobsExplorer;
  newIntakesForDeletion: DeleteIntakeRequest[];
  setNewIntakesForDeletion: React.Dispatch<React.SetStateAction<DeleteIntakeRequest[]>>;
  newIntakesForExport: Intake[];
  setNewIntakesForExport: React.Dispatch<React.SetStateAction<Intake[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const IntakesTable: React.FC<IntakesTableProps> = ({
  organizationId,
  activeJobsExplorer,
  newIntakesForDeletion,
  setNewIntakesForDeletion,
  newIntakesForExport,
  setNewIntakesForExport,
  userRole,
  isAssignedUser,
}) => {
  const intakesRepository = new IntakesRepository();

  const config = useMemo<MigrationEntityTableConfig<Intake>>(() => ({
    entityName: "intakes",
    searchLabel: "Search intakes",
    searchPlaceholder: "Intake number",
    emptyMessage: "No intakes found.",
    entityKeySortProperty: "intakeNumber",
    queryKey: QUERY_KEYS.INTAKES,
    detailQueryKey: QUERY_KEYS.INTAKE,

    getEntityKey: (i) => i.intakeNumber,
    getDetailPath: (orgId, entityId) => `/organization/${orgId}/intake/${entityId}`,

    fetchEntities: (params) => intakesRepository.getIntakes({
      organizationId: params.organizationId,
      intakeNumber: params.search,
      descending: params.descending,
      orderBy: params.orderBy,
      status: params.status,
      isLocked: params.isLocked,
      hasFlaggedPaths: params.hasFlaggedPaths,
      hasConflicts: params.hasConflicts,
      flagStatus: params.flagStatus,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    }),

    deleteExplorerValue: ActiveIntakeJobsExplorer.DeleteIntakes,
    exportExplorerValue: ActiveIntakeJobsExplorer.GenerateCSV,

    createDeleteRequest: (entity, orgId) => ({
      migrationEntityId: entity.id,
      intakeNumber: entity.intakeNumber,
      status: JobRequestStatus.New,
      type: JobType.Delete,
      entityType: JobEntityType.Intake,
      organizationId: orgId,
    } as DeleteIntakeRequest),

    updateFlag: (entityId, flagStatus) => intakesRepository.updateIntakeFlag(entityId, flagStatus),
    updateBlock: (entityId, isBlocked) => intakesRepository.updateIntakeBlock(entityId, isBlocked),
    blockConfig: MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS.INTAKE,

    renderFlagModal: ({ open, entityKey, flagStatus, onClose, onConfirm }) => (
      <ConfirmIntakeFlagUpdateModal
        open={open}
        intakeNumber={entityKey}
        flagStatus={flagStatus}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    ),
  }), [intakesRepository]);

  return (
    <MigrationEntityTable<Intake>
      config={config}
      organizationId={organizationId}
      activeJobsExplorer={activeJobsExplorer}
      newForDeletion={newIntakesForDeletion}
      setNewForDeletion={setNewIntakesForDeletion}
      newForExport={newIntakesForExport}
      setNewForExport={setNewIntakesForExport}
      userRole={userRole}
      isAssignedUser={isAssignedUser}
    />
  );
};

export default IntakesTable;
